import type { Timestamp } from '@firebase/firestore';
import type { ValouAreaId } from './valou';

// Re-export Valou types
export * from './valou';

// ============================================
// REALMS (GoodLife vs GoodWork)
// ============================================

export type Realm = 'goodlife' | 'goodwork';

export interface RealmInfo {
  id: Realm;
  label: string;
  description: string;
  color: string;       // Primary color
  colorLight: string;  // Light background
}

export const REALMS: RealmInfo[] = [
  {
    id: 'goodlife',
    label: 'GoodLife',
    description: 'Orte fuer Freizeit & Begegnung',
    color: 'emerald',
    colorLight: 'emerald-50',
  },
  {
    id: 'goodwork',
    label: 'GoodWork',
    description: 'Orte fuer Arbeit & Kreativitaet',
    color: 'indigo',
    colorLight: 'indigo-50',
  },
];

// ============================================
// ENUMS & UNION TYPES
// ============================================

export type PlaceStatus = 'pending' | 'approved' | 'rejected';

export type PlaceVisibility = 'private' | 'public';

// PlaceType entfernt - ersetzt durch MainCategory

// ============================================
// MAIN CATEGORIES (Emotionale Kategorien)
// ============================================

export type MainCategory =
  | 'urban_creative'      // Urbane Oasen & Kreatives
  | 'public_living_room'  // Öffentliche Wohnzimmer
  | 'silent_sanctuary'    // Stille & Rückzug
  | 'analoge_refugien'    // Analoge Refugien
  | 'natur_outdoor';      // Natur & Outdoor

export interface MainCategoryInfo {
  id: MainCategory;
  label: string;
  description: string;
  icon: string;
  color: string;  // Tailwind color class
}

export const MAIN_CATEGORIES: MainCategoryInfo[] = [
  {
    id: 'urban_creative',
    label: 'Urbane Oasen & Kreatives',
    description: 'Kreative Räume in der Stadt',
    icon: 'Palette',
    color: 'bg-gray-700',
  },
  {
    id: 'public_living_room',
    label: 'Öffentliche Wohnzimmer',
    description: 'Orte der anlasslosen Vergemeinschaftung',
    icon: 'Sofa',
    color: 'bg-gray-700',
  },
  {
    id: 'silent_sanctuary',
    label: 'Stille & Rückzug',
    description: 'Pausentaste drücken, wahrnehmen',
    icon: 'Moon',
    color: 'bg-gray-700',
  },
  {
    id: 'analoge_refugien',
    label: 'Analoge Refugien',
    description: 'Hier spürt man das echte Leben mit Geschichte',
    icon: 'Clock',
    color: 'bg-gray-700',
  },
  {
    id: 'natur_outdoor',
    label: 'Natur & Outdoor',
    description: 'Elementarerfahrungen, draußen sein',
    icon: 'Trees',
    color: 'bg-gray-700',
  },
];

// ============================================
// VIBE TAGS (Filter-Tags)
// ============================================

export type VibeTag =
  | 'rollenlos'              // Hier musst du weder Chef noch Kunde sein
  | 'schnack_potential'      // Hier kommst du leicht ins Gespräch
  | 'unverfuegbares_glueck'  // Momente, die man nicht bestellen kann
  | 'offen_fuer_alle'        // Keine VIP-Bereiche, Geldbeutel egal
  | 'selbstwirksam'          // Hier kannst du dich einbringen
  | 'antiquiert_gut'         // Charme statt Hochglanz
  | 'empfangsbereit'         // Sendeleistung runter, wahrnehmen
  | 'anlasslos'              // Kein Grund nötig, verweilen erlaubt
  | 'selbstueberraschung';   // Etwas Neues über dich erfahren

export interface VibeTagInfo {
  id: VibeTag;
  label: string;
  description: string;
  icon: string;
}

export const VIBE_TAGS: VibeTagInfo[] = [
  { id: 'rollenlos', label: '#rollenlos', description: 'Hier musst du weder Chef noch Kunde sein. Einfach Mensch.', icon: 'UserCircle' },
  { id: 'schnack_potential', label: '#schnack-potential', description: 'Hier kommst du leicht ins Gespräch. Small Talk erwünscht.', icon: 'MessageCircle' },
  { id: 'unverfuegbares_glueck', label: '#unverfuegbares-glueck', description: 'Orte für Momente, die man nicht bestellen kann.', icon: 'Sparkles' },
  { id: 'offen_fuer_alle', label: '#offen-fuer-alle', description: 'Keine VIP-Bereiche. Dein Geldbeutel spielt keine Rolle.', icon: 'DoorOpen' },
  { id: 'selbstwirksam', label: '#selbstwirksam', description: 'Hier kannst du dich einbringen und etwas bewirken.', icon: 'Hammer' },
  { id: 'antiquiert_gut', label: '#antiquiert-und-gut', description: 'Charme statt Hochglanz. Beständigkeit statt Innovation.', icon: 'History' },
  { id: 'empfangsbereit', label: '#empfangsbereit', description: 'Fahre deine Sendeleistung runter. Hier geht es ums Wahrnehmen.', icon: 'Ear' },
  { id: 'anlasslos', label: '#anlasslos', description: 'Du brauchst keinen Grund, um hier zu sein. Verweilen erlaubt.', icon: 'Coffee' },
  { id: 'selbstueberraschung', label: '#selbstueberraschung', description: 'Orte, die dich einladen, etwas Neues über dich zu erfahren.', icon: 'Lightbulb' },
];

// ============================================
// GOODWORK CATEGORIES (Arbeitsorte)
// ============================================

export type GoodWorkCategory =
  | 'coworking'          // Coworking Space
  | 'focused_work'       // Fokusraum
  | 'creative_studio'    // Kreativstudio
  | 'meeting_space'      // Meeting & Events
  | 'maker_space'        // Maker & Werkstatt
  | 'learning_hub';      // Lern- & Bildungsort

export interface GoodWorkCategoryInfo {
  id: GoodWorkCategory;
  label: string;
  description: string;
  icon: string;
  color: string;
}

export const GOODWORK_CATEGORIES: GoodWorkCategoryInfo[] = [
  {
    id: 'coworking',
    label: 'Coworking Space',
    description: 'Gemeinsam arbeiten, Netzwerken',
    icon: 'Users',
    color: 'bg-indigo-600',
  },
  {
    id: 'focused_work',
    label: 'Fokusraum',
    description: 'Konzentriertes Arbeiten, wenig Ablenkung',
    icon: 'Target',
    color: 'bg-indigo-600',
  },
  {
    id: 'creative_studio',
    label: 'Kreativstudio',
    description: 'Fuer kreative Berufe, Workshops',
    icon: 'Palette',
    color: 'bg-indigo-600',
  },
  {
    id: 'meeting_space',
    label: 'Meeting & Events',
    description: 'Besprechungen, Workshops, Events',
    icon: 'Calendar',
    color: 'bg-indigo-600',
  },
  {
    id: 'maker_space',
    label: 'Maker & Werkstatt',
    description: 'Handwerk, Prototyping, DIY',
    icon: 'Wrench',
    color: 'bg-indigo-600',
  },
  {
    id: 'learning_hub',
    label: 'Lern- & Bildungsort',
    description: 'Kurse, Weiterbildung, Bibliotheken',
    icon: 'GraduationCap',
    color: 'bg-indigo-600',
  },
];

// ============================================
// GOODWORK VIBE TAGS
// ============================================

export type GoodWorkVibeTag =
  | 'fokussiert'         // Ruhige Umgebung fuer Deep Work
  | 'kollaborativ'       // Ideal fuer Teamarbeit
  | 'inspirierend'       // Kreative Atmosphaere
  | 'flexibel'           // Stundenweise buchbar
  | 'professionell'      // Business-tauglich
  | 'community'          // Starke Gemeinschaft
  | 'gruendertauglich'   // Startup-freundlich
  | 'nachhaltig'         // Oekologisch, sozial
  | 'outdoor'            // Arbeiten im Freien moeglich
  | 'naturverbunden';    // Gruene Umgebung, natuerliche Atmosphaere

export interface GoodWorkVibeTagInfo {
  id: GoodWorkVibeTag;
  label: string;
  description: string;
  icon: string;
}

export const GOODWORK_VIBE_TAGS: GoodWorkVibeTagInfo[] = [
  { id: 'fokussiert', label: '#fokussiert', description: 'Ruhige Umgebung fuer Deep Work und konzentriertes Arbeiten.', icon: 'Focus' },
  { id: 'kollaborativ', label: '#kollaborativ', description: 'Ideal fuer Teamarbeit, Austausch und gemeinsame Projekte.', icon: 'Users' },
  { id: 'inspirierend', label: '#inspirierend', description: 'Kreative Atmosphaere, schoenes Ambiente, inspirierende Umgebung.', icon: 'Sparkles' },
  { id: 'flexibel', label: '#flexibel', description: 'Stundenweise buchbar, keine langfristige Bindung noetig.', icon: 'Clock' },
  { id: 'professionell', label: '#professionell', description: 'Business-tauglich, gute Infrastruktur, repraesentativ.', icon: 'Briefcase' },
  { id: 'community', label: '#community', description: 'Starke Gemeinschaft, regelmaessige Events, aktives Networking.', icon: 'Heart' },
  { id: 'gruendertauglich', label: '#gruendertauglich', description: 'Startup-freundlich, Mentoring, Ressourcen fuer Gruender.', icon: 'Rocket' },
  { id: 'nachhaltig', label: '#nachhaltig', description: 'Oekologisch bewusst, sozial verantwortlich, faire Bedingungen.', icon: 'Leaf' },
  { id: 'outdoor', label: '#outdoor', description: 'Arbeiten im Freien moeglich, Terrasse oder Garten verfuegbar.', icon: 'Sun' },
  { id: 'naturverbunden', label: '#naturverbunden', description: 'Gruene Umgebung, natuerliche Atmosphaere, naturnahe Lage.', icon: 'Trees' },
];

// ============================================
// GOODWORK PLACE FEATURES (Ausstattung, Preise, etc.)
// ============================================

export type WifiQuality = 'none' | 'basic' | 'fast' | 'excellent';
export type PowerOutletAvailability = 'few' | 'some' | 'many';

export interface PlaceAmenities {
  wifi?: WifiQuality;
  powerOutlets?: PowerOutletAvailability;
  monitors?: boolean;
  printer?: boolean;
  kitchen?: boolean;
  meetingRooms?: boolean;
  phoneBooths?: boolean;
  locker?: boolean;
}

export interface PlacePricing {
  hourly?: number;           // EUR pro Stunde
  daily?: number;            // EUR pro Tag
  monthly?: number;          // EUR pro Monat (Mitgliedschaft)
  dropIn?: boolean;          // Spontan moeglich ohne Buchung
  bookingUrl?: string;       // Link zur Buchung
  priceNote?: string;        // Freitext fuer Besonderheiten
}

export interface PlaceWorkspaces {
  totalSeats?: number;       // Gesamtzahl Arbeitsplaetze
  quietZone?: boolean;       // Ruhebereich vorhanden
  teamArea?: boolean;        // Teambereich vorhanden
  standingDesks?: boolean;   // Steharbeitsplaetze
  outdoorSeats?: number;     // Arbeitsplaetze draussen
}

export interface PlaceGoodworkEvents {
  networking?: boolean;      // Networking Events
  workshops?: boolean;       // Workshops & Kurse
  coworking?: boolean;       // Coworking Sessions
  community?: string;        // Community-Beschreibung
}

// Labels fuer UI-Anzeige
export const WIFI_QUALITY_LABELS: Record<WifiQuality, string> = {
  none: 'Kein WLAN',
  basic: 'Einfaches WLAN',
  fast: 'Schnelles WLAN',
  excellent: 'Sehr schnelles WLAN',
};

export const POWER_OUTLET_LABELS: Record<PowerOutletAvailability, string> = {
  few: 'Wenige Steckdosen',
  some: 'Einige Steckdosen',
  many: 'Viele Steckdosen',
};

export const AMENITY_LABELS: Record<keyof PlaceAmenities, string> = {
  wifi: 'WLAN',
  powerOutlets: 'Steckdosen',
  monitors: 'Externe Monitore',
  printer: 'Drucker/Scanner',
  kitchen: 'Kueche/Kaffee',
  meetingRooms: 'Meeting-Raeume',
  phoneBooths: 'Telefonkabinen',
  locker: 'Schliessfaecher',
};

// ============================================
// GOODWORK MOODS & ACTIVITIES
// ============================================

export type GoodWorkMoodLabel =
  | 'Produktiv & fokussiert'
  | 'Kreativ & experimentell'
  | 'Kollaborativ & vernetzt'
  | 'Ruhig & konzentriert'
  | 'Professionell & repraesentativ';

export type GoodWorkActivityLabel =
  | 'Deep Work'
  | 'Meetings & Calls'
  | 'Kreativ arbeiten'
  | 'Netzwerken & Socializing'
  | 'Lernen & Weiterbildung'
  | 'Workshops & Events'
  | 'Praesentationen';

// ============================================
// COMBINED CATEGORY TYPE (fuer einfache Verwendung)
// ============================================

export type AnyCategory = MainCategory | GoodWorkCategory;
export type AnyVibeTag = VibeTag | GoodWorkVibeTag;

// Helper: Alle Kategorien-Infos kombiniert
export const ALL_CATEGORIES = [...MAIN_CATEGORIES, ...GOODWORK_CATEGORIES];

// ============================================
// ATMOSPHERE TYPES (Sensorische Hinweise)
// ============================================

export type AtmosphereType =
  | 'natural_light'    // Natürliches Licht
  | 'cozy_lighting'    // Gemütlich beleuchtet
  | 'plants'           // Viele Pflanzen
  | 'quiet'            // Angenehm ruhig
  | 'music'            // Gute Musik
  | 'outdoor'          // Draußen
  | 'fireplace'        // Mit Feuer/Kamin
  | 'water_view'       // Am Wasser
  | 'city_view';       // Stadtblick

export interface AtmosphereInfo {
  id: AtmosphereType;
  label: string;
  emoji: string;
}

export const ATMOSPHERE_TYPES: AtmosphereInfo[] = [
  { id: 'natural_light', label: 'Natürliches Licht', emoji: '☀️' },
  { id: 'cozy_lighting', label: 'Gemütlich', emoji: '🕯️' },
  { id: 'plants', label: 'Viele Pflanzen', emoji: '🌿' },
  { id: 'quiet', label: 'Angenehm ruhig', emoji: '🔇' },
  { id: 'music', label: 'Gute Musik', emoji: '🎵' },
  { id: 'outdoor', label: 'Draußen', emoji: '🏕️' },
  { id: 'fireplace', label: 'Mit Kamin', emoji: '🔥' },
  { id: 'water_view', label: 'Am Wasser', emoji: '💧' },
  { id: 'city_view', label: 'Stadtblick', emoji: '🌆' },
];

export type MoodLabel =
  | 'Ruhig & entspannt'
  | 'Lebendig & gesellig'
  | 'Kreativ & inspirierend'
  | 'Gemütlich & vertraut'
  | 'Offen & einladend'
  | 'Traditionell & authentisch';

export type ActivityLabel =
  | 'Einfach da sein'
  | 'Lesen & Schmökern'
  | 'Plaudern & Zuhören'
  | 'Spielen (Karten, Brettspiele)'
  | 'Neue Menschen kennenlernen'
  | 'Stammtisch besuchen'
  | 'Events & Veranstaltungen'
  | 'Kreativ sein';

export type CommunityFeature =
  | 'Stammtisch vorhanden'
  | 'Regelmäßige Treffen'
  | 'Offene Runde'
  | 'Nachbarschaftstreff'
  | 'Generationen-Mix'
  | 'Haustiere willkommen'
  | 'Kinder willkommen'
  | 'Barrierefrei';

export type PlaceMatchView =
  | 'search'
  | 'search-map'      // Map view for places
  | 'detail'
  | 'favorites'
  | 'createIntro'     // Intro page before place creation
  | 'create'
  | 'myPlaces'
  | 'moderation'
  | 'admin'
  | 'logbook'         // Personal place notes / GoodPlace Logbook
  | 'community'       // Search/browse other users
  | 'communityEvents' // Community-wide events view
  | 'publicProfile'   // View another user's public profile
  | 'privacySettings' // User's own privacy settings
  | 'messages'        // Message inbox / conversation list
  | 'chat'            // Single chat conversation
  | 'groups'          // Groups/Clubs explore page
  | 'groupDetail'     // Single group view
  | 'createGroup'     // Create new group wizard
  | 'myGroups'        // User's groups list
  | 'userSearch'      // Search users (community member search)
  | 'magazine'        // Magazine / articles overview
  | 'articleDetail'   // Single article view
  | 'concierge';      // AI-powered place matching concierge

// ============================================
// GEOCODED LOCATION (for Google Maps integration)
// ============================================

export interface GeocodedLocation {
  latitude: number;
  longitude: number;
  formattedAddress: string;
  district?: string;
  city?: string;
  postalCode?: string;
  placeId?: string;
}

// ============================================
// OPENING HOURS
// ============================================

export interface OpeningHours {
  monday?: string;
  tuesday?: string;
  wednesday?: string;
  thursday?: string;
  friday?: string;
  saturday?: string;
  sunday?: string;
  displayText: string;
}

// ============================================
// PLACE
// ============================================

export interface Place {
  id: string;
  name: string;
  address?: string;      // Optional - kann per Reverse-Geocoding ermittelt werden
  postalCode?: string;   // Optional
  city?: string;         // Optional
  district?: string;     // Stadtteil z.B. "Maxvorstadt"
  tagline: string;
  description?: string;  // Optional - "Weitere Informationen"
  goodFactor?: string;  // "Darum ist das ein GoodPlace" - Kurzes Highlight
  pitch?: string;       // Ausführlicher Pitch-Text für Detailseite
  imageUrl?: string;
  images?: string[];
  moods: MoodLabel[];
  activities: ActivityLabel[];
  community: CommunityFeature[];
  openingHours: OpeningHours;

  // Realms: GoodLife (Freizeit) und/oder GoodWork (Arbeit)
  // Ein Ort kann in beiden Realms sein (z.B. Cafe das auch Arbeitsplatz ist)
  realms?: Realm[];      // Array: ['goodlife'] oder ['goodlife', 'goodwork']

  // Kategorien (realm-spezifisch)
  category: MainCategory;                    // GoodLife-Hauptkategorie (Pflicht wenn 'goodlife' in realms)
  categories?: MainCategory[];               // GoodLife-Kategorien (mehrere moeglich)
  goodworkCategory?: GoodWorkCategory;       // GoodWork-Hauptkategorie (Pflicht wenn 'goodwork' in realms)
  goodworkCategories?: GoodWorkCategory[];   // GoodWork-Kategorien (mehrere moeglich)

  // Vibe-Tags (realm-spezifisch)
  vibeTags?: VibeTag[];                      // GoodLife Vibe-Tags
  goodworkVibeTags?: GoodWorkVibeTag[];      // GoodWork Vibe-Tags
  atmosphere?: AtmosphereType[];             // Sensorische Hinweise (optional)

  // GoodWork-spezifische Felder (alle optional)
  goodworkFactor?: string;                   // "Warum ist das ein GoodWork Ort?"
  amenities?: PlaceAmenities;                // Ausstattung (WLAN, Steckdosen, etc.)
  pricing?: PlacePricing;                    // Preise und Buchung
  workspaces?: PlaceWorkspaces;              // Arbeitsplaetze
  goodworkEvents?: PlaceGoodworkEvents;      // Events & Community

  // Computed from reviews
  rating: number;
  reviewCount: number;

  // Moderation
  status: PlaceStatus;
  createdBy: string;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
  approvedBy?: string;
  approvedAt?: Timestamp | Date;
  rejectionReason?: string;

  // Visibility & Sharing
  visibility: PlaceVisibility;   // 'private' oder 'public'
  sharedWith?: string[];         // User-IDs (fuer private Orte)

  // Location
  latitude?: number;
  longitude?: number;

  // Contact
  website?: string;
  phone?: string;
  email?: string;

  // Valou Integration
  valouAreas?: ValouAreaId[];           // Manuell ausgewählt
  computedValouAreas?: ValouAreaId[];   // Automatisch berechnet

  // Einreicher-Anzeige
  showSubmitter?: boolean;              // Ob Einreicher angezeigt werden soll
  submitterName?: string;               // Name des Einreichers (wenn showSubmitter true)

  // Verifikations-Notizen (aus KI-Verifikation)
  verificationNotes?: string;           // Beschreibende Infos die nicht in strukturierte Felder passen

  // Freitext für Ausstattung und Möglichkeiten (GoodWork)
  amenitiesText?: string;               // Freitext statt strukturierter WLAN/Steckdosen/Plätze-Felder
}

// For creating a new place (without computed/auto fields)
export type PlaceCreateInput = Omit<
  Place,
  'id' | 'rating' | 'reviewCount' | 'status' | 'createdBy' | 'createdAt' | 'updatedAt' | 'approvedBy' | 'approvedAt' | 'rejectionReason' | 'sharedWith'
>;

// ============================================
// REVIEW
// ============================================

export type ReviewStatus = 'pending' | 'approved' | 'flagged' | 'hidden';

export interface Review {
  id: string;
  placeId: string;
  userId: string;
  userName: string;
  userProfilePicture?: string;
  rating: number; // 1-5
  text: string;
  visitDate?: string;
  createdAt: Timestamp | Date;
  updatedAt?: Timestamp | Date;
  helpful: number;
  helpfulVoters: string[];

  // Moderation fields
  status?: ReviewStatus;      // Default: 'approved' for backwards compatibility
  flagCount?: number;
  flaggedBy?: string[];
  moderatorNote?: string;
  moderatedAt?: Timestamp | Date;
  moderatedBy?: string;
}

export type ReviewCreateInput = Omit<
  Review,
  'id' | 'createdAt' | 'updatedAt' | 'helpful' | 'helpfulVoters' | 'status' | 'flagCount' | 'flaggedBy' | 'moderatorNote' | 'moderatedAt' | 'moderatedBy'
>;

// ============================================
// EVENT
// ============================================

export interface PlaceEvent {
  id: string;
  placeId: string;
  name: string;
  day: string;
  time: string;
  description?: string;
  recurring: boolean;
  createdBy: string;
  createdAt: Timestamp | Date;
}

export type EventCreateInput = Omit<PlaceEvent, 'id' | 'createdAt'>;

// ============================================
// USER FAVORITES
// ============================================

export interface UserFavorites {
  userId: string;
  placeIds: string[];
  updatedAt: Timestamp | Date;
}

// ============================================
// VALOU FEEDBACK (User-Feedback zu Ort-Nutzung)
// ============================================

export type ValouFeedbackSource = 'favorite' | 'checkin';

export interface ValouFeedback {
  id: string;
  placeId: string;
  userId: string;
  areas: ValouAreaId[];           // Gewaehlte Valou-Bereiche
  source: ValouFeedbackSource;    // Woher kam das Feedback
  createdAt: Timestamp | Date;
  updatedAt?: Timestamp | Date;
}

export type ValouFeedbackCreateInput = Omit<ValouFeedback, 'id' | 'createdAt' | 'updatedAt'>;

// Verstaendliche Labels fuer Valou-Bereiche (fuer Feedback-UI)
export interface ValouFeedbackLabel {
  areaId: ValouAreaId;
  label: string;
  color: string;
}

export const VALOU_FEEDBACK_LABELS: ValouFeedbackLabel[] = [
  { areaId: 'privatesLeben', label: 'Rueckzug', color: '#7CB342' },
  { areaId: 'persoenlichkeitSkills', label: 'Inspiration', color: '#FFC107' },
  { areaId: 'taetigkeit', label: 'Arbeiten', color: '#A1887F' },
  { areaId: 'stilWirkung', label: 'Auftanken', color: '#EC407A' },
  { areaId: 'ressourcenMittel', label: 'Begegnung', color: '#42A5F5' },
  { areaId: 'gesundheit', label: 'Bewegung', color: '#EF5350' },
];

// ============================================
// MODERATION
// ============================================

export interface Moderator {
  userId: string;
  email: string;
  name: string;
  addedAt: Timestamp | Date;
  addedBy: string;
}

export interface ModerationAction {
  id: string;
  placeId: string;
  moderatorId: string;
  action: 'approve' | 'reject';
  reason?: string;
  timestamp: Timestamp | Date;
}

// ============================================
// PLACE SUGGESTIONS (User Change Requests)
// ============================================

export type SuggestionStatus = 'pending' | 'approved' | 'rejected' | 'partial';

export interface PlaceSuggestion {
  id: string;
  placeId: string;                    // Referenz zum existierenden Ort
  placeName: string;                  // Denormalisiert fuer schnelle Anzeige
  changes: Partial<PlaceCreateInput>; // Nur geaenderte Felder
  suggestedBy: string;                // User ID
  suggestedByName: string;            // User Display Name
  suggestedByEmail?: string;          // User Email
  status: SuggestionStatus;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
  reviewedBy?: string;                // Moderator/Admin ID
  reviewedAt?: Timestamp | Date;
  reviewNote?: string;                // Notiz bei Ablehnung/Partial
  approvedFields?: string[];          // Fuer Partial Approval
}

export type SuggestionCreateInput = Omit<
  PlaceSuggestion,
  'id' | 'status' | 'createdAt' | 'updatedAt' | 'reviewedBy' | 'reviewedAt' | 'reviewNote' | 'approvedFields'
>;

// ============================================
// FILTERS
// ============================================

export interface PlaceFilter {
  searchWhat: string;
  searchWhere: string;
  moods: MoodLabel[];
  activities: ActivityLabel[];
  community: CommunityFeature[];
  openNow?: boolean;
  minRating?: number;
  // Realm Filter (GoodLife vs GoodWork)
  realm?: Realm;
  // Emotionale Filter (realm-spezifisch)
  category?: MainCategory | GoodWorkCategory | '';
  vibeTags?: VibeTag[];
  goodworkVibeTags?: GoodWorkVibeTag[];
  // Location-based search
  searchRadius?: number;  // Radius in km (default 5)
  searchLocation?: GeocodedLocation | null;  // Selected/geocoded location
}

export const INITIAL_FILTERS: PlaceFilter = {
  searchWhat: '',
  searchWhere: '',
  moods: [],
  activities: [],
  community: [],
  realm: 'goodlife',  // Default: GoodLife
  category: '',
  vibeTags: [],
  goodworkVibeTags: [],
};

// ============================================
// COMPONENT PROPS
// ============================================

export interface GoodPlaceMatchProps {
  // Standalone mode (default: true)
  standalone?: boolean;

  // Integration mode props
  onNavigateBack?: () => void;
  user?: {
    uid: string;
    email?: string | null;
    displayName?: string | null;
    photoURL?: string | null;
  } | null;
  showAppNotification?: (
    message: string,
    type?: 'success' | 'info' | 'error',
    duration?: number
  ) => void;

  // Initial state
  initialView?: PlaceMatchView;
  initialPlaceId?: string;
}

// ============================================
// CONSTANTS DATA
// ============================================

export interface MoodOption {
  label: MoodLabel;
  icon: string;
}

export interface ActivityOption {
  label: ActivityLabel;
  icon: string;
}

export interface CommunityOption {
  label: CommunityFeature;
  icon: string;
}

// ============================================
// PLACE NOTES / LOGBOOK
// ============================================

// Foto-Metadaten für Logbuch-Einträge
export interface PhotoMetadata {
  url: string;
  caption?: string;
  thumbnailUrl?: string;
  takenAt?: Timestamp | Date;
}

// Logbuch-Stimmungs-Kategorien (für persönliche Notizen)
export type LogbookMoodCategory = 'positive' | 'neutral' | 'mixed' | 'special' | 'energy';

// Logbuch-Stimmungs-Option (für persönliche Notizen)
export interface LogbookMoodOption {
  value: string;
  label: string;
  emoji: string;
  category: LogbookMoodCategory;
}

export interface PlaceNote {
  id: string;
  placeId: string;
  placeName: string;          // Denormalisiert fuer schnelle Anzeige
  placeCategory?: AnyCategory;  // Supports both GoodLife and GoodWork categories
  userId: string;
  text: string;
  personalRating?: number;    // Persönliche Bewertung (1-5)
  visitDate?: Timestamp | Date;
  mood?: string;              // Legacy: Einzelne Stimmung (backward-kompatibel)
  moods?: string[];           // Neu: Mehrere Stimmungen
  photos?: string[];          // Legacy: Nur URLs
  photoMetadata?: PhotoMetadata[]; // Neu: Fotos mit Metadaten
  isPublic?: boolean;         // Öffentlich geteilt (default: false)
  sharedAt?: Timestamp | Date; // Wann öffentlich gemacht
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
}

export type PlaceNoteCreateInput = Omit<
  PlaceNote,
  'id' | 'createdAt' | 'updatedAt'
>;

// ============================================
// USER PRIVACY SETTINGS
// ============================================

export type MessagePrivacySetting = 'everyone' | 'mutual' | 'none';

export interface PrivacySettings {
  searchable: boolean;        // Kann von anderen Usern gefunden werden
  showCheckins: boolean;      // Check-ins öffentlich sichtbar
  showFavorites: boolean;     // Favoriten öffentlich sichtbar
  showReviews: boolean;       // Reviews öffentlich sichtbar
  allowMessages: MessagePrivacySetting;  // Wer darf Nachrichten senden
}

export const DEFAULT_PRIVACY_SETTINGS: PrivacySettings = {
  searchable: false,
  showCheckins: false,
  showFavorites: false,
  showReviews: true,
  allowMessages: 'everyone',  // Default: jeder kann schreiben
};

// ============================================
// PUBLIC USER PROFILE
// ============================================

// GoodWork Crafting Integration Data
export interface CraftingData {
  personalityType?: string;
  motivationFactors?: string[];
  riasecType?: string;
  valouPreferences?: {
    vorlieben: string[];
    abneigungen: string[];
    mustHaves: string[];
    noGos: string[];
  };
  valouMoods?: Record<string, number>;
  averageMentalLoad?: number;
  currentWellbeing?: number;
  lastSyncedAt?: Timestamp | Date;
}

export interface PublicUserProfile {
  id: string;
  displayName: string;
  displayNameLower?: string;        // Für Suche
  photoURL?: string;
  bio?: string;                     // Max 200 Zeichen
  interests?: string[];             // Interessen-Tags
  privacy: PrivacySettings;
  joinedAt?: Timestamp | Date;
  // Stats (nur wenn privacy erlaubt)
  reviewCount?: number;
  favoriteCount?: number;
  // GoodWork Crafting Integration
  craftingLinked?: boolean;
  craftingData?: CraftingData;
}
